import { posthogRequestHeaders } from '../lib/posthog';

const API = '/api';

async function req(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...posthogRequestHeaders(),
      ...(options.headers || {}),
    },
    ...options,
  });
  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { detail: text };
    }
  }
  if (!res.ok) {
    const detail = data?.detail;
    const msg = typeof detail === 'string' ? detail : detail?.[0]?.msg || res.statusText;
    const err = new Error(msg || 'Request failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const authApi = {
  me: () => req('/auth/me'),
  login: (email, password) =>
    req('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => req('/auth/logout', { method: 'POST', body: '{}' }),
  verify2fa: (code) =>
    req('/auth/2fa/verify', { method: 'POST', body: JSON.stringify({ code }) }),
  setup2fa: () => req('/auth/2fa/setup', { method: 'POST', body: '{}' }),
  enable2fa: (code) =>
    req('/auth/2fa/enable', { method: 'POST', body: JSON.stringify({ code }) }),
  forgot: (email) =>
    req('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  reset: (token, password) =>
    req('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) }),
  getEula: () => req('/auth/eula'),
  acceptEula: () => req('/auth/eula/accept', { method: 'POST', body: '{}' }),
  listUsers: () => req('/admin/users'),
  createUser: (body) => req('/admin/users', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (id, body) =>
    req(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteUser: (id) => req(`/admin/users/${id}`, { method: 'DELETE' }),
  adminReset: (id) => req(`/admin/users/${id}/reset-password`, { method: 'POST', body: '{}' }),
  listDealDrafts: () => req('/admin/deal-drafts'),
  createDealDraft: (body) =>
    req('/admin/deal-drafts', { method: 'POST', body: JSON.stringify(body) }),
  updateDealDraft: (dealId, body) =>
    req(`/admin/deal-drafts/${encodeURIComponent(dealId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteDealDraft: (dealId) =>
    req(`/admin/deal-drafts/${encodeURIComponent(dealId)}`, { method: 'DELETE' }),
};

// Log evidence (SPEC-55): internal-only + deal-scoped. dealId is required so a
// Juno/Sedgwick session cannot list or stream Eagle Boswell / Bald Prairie logs.
function evidenceDealQuery(dealId) {
  const id = String(dealId || '').trim();
  if (!id) {
    const err = new Error('Evidence dealId required');
    err.status = 400;
    throw err;
  }
  return `dealId=${encodeURIComponent(id)}`;
}

export const evidenceApi = {
  index: (dealId) => req(`/evidence/index?${evidenceDealQuery(dealId)}`),
  fileUrl: (id, dealId) =>
    `${API}/evidence/files/${encodeURIComponent(id)}?${evidenceDealQuery(dealId)}`,
  // Fetch as a blob so the session cookie is sent, then open an object URL.
  open: async (id, dealId) => {
    const res = await fetch(
      `${API}/evidence/files/${encodeURIComponent(id)}?${evidenceDealQuery(dealId)}`,
      { credentials: 'include' },
    );
    if (!res.ok) {
      const err = new Error(`Evidence ${res.status}`);
      err.status = res.status;
      throw err;
    }
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
};

export const assistantApi = {
  chat: (body) => req('/assistant/chat', { method: 'POST', body: JSON.stringify(body) }),
  /** Idempotent personal-agent session (agentId + threadId). Shared by VDR + ABS. */
  createSession: (body = {}) =>
    req('/assistant/sessions', { method: 'POST', body: JSON.stringify(body) }),
  /** Hydrate messages for reconnect (< episodic TTL). */
  getThreadMessages: (threadId) =>
    req(`/assistant/threads/${encodeURIComponent(threadId)}/messages`),
  /**
   * SSE stream POST. Returns the raw Response so callers can parse the locked
   * {type:text|done|error} contract via parseSseDataLine / readAssistantSse.
   */
  /** Role-filtered guide catalog. Unknown or hidden guides are absent. */
  listGuides: (page) =>
    req(`/assistant/guides${page ? `?page=${encodeURIComponent(page)}` : ''}`),
  guidesForPage: (dashboardKey) =>
    req(`/assistant/guides/for-page/${encodeURIComponent(dashboardKey)}`),
  /** 404 when the id is unknown or hidden. The body does not include a title then. */
  getGuide: (id) => req(`/assistant/guides/${encodeURIComponent(id)}`),
  stream: async (body, options = {}) => {
    const res = await fetch(`${API}/assistant/stream`, {
      method: 'POST',
      credentials: 'include',
      signal: options.signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...posthogRequestHeaders(),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let data = null;
      const text = await res.text();
      if (text) {
        try {
          data = JSON.parse(text);
        } catch {
          data = { detail: text };
        }
      }
      const detail = data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.[0]?.msg || res.statusText;
      const err = new Error(msg || 'Stream failed');
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return res;
  },
  listConversations: () => req('/assistant/conversations'),
  getConversation: (id) => req(`/assistant/conversations/${id}`),
  appendTurn: (body) =>
    req('/assistant/conversations/turns', { method: 'POST', body: JSON.stringify(body) }),
  deleteConversation: (id) => req(`/assistant/conversations/${id}`, { method: 'DELETE' }),
};

export const wellNotesApi = {
  list: (wellKey) => req(`/wells/${encodeURIComponent(wellKey)}/notes`),
  add: (wellKey, body) =>
    req(`/wells/${encodeURIComponent(wellKey)}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  remove: (noteId) => req(`/wells/notes/${noteId}`, { method: 'DELETE' }),
};
